import { memo } from 'react';
import { Box, Group, ScrollArea, Text } from '@mantine/core';
import { ChildThumbnail } from './ChildThumbnail';
import { HOME_ROW_CEILING, type CategoryChildView } from '../AppContentUtils';

interface HomeRowPreviewProps {
  title: string;
  children: CategoryChildView[];
}

const TILE_W = 144;

/**
 * How this category's row appears on Home: 16:9 tiles in the operator's
 * order with the title under each (the app captions every row that holds
 * items). A cheap strip — fixed tile boxes, lazy images, memoized on the
 * resolved views — not the TV frame (the App page has that).
 */
function HomeRowPreviewInner({ title, children }: HomeRowPreviewProps) {
  const shown = children.slice(0, HOME_ROW_CEILING);
  const overflow = children.length - shown.length;
  return (
    <Box
      p="md"
      style={{
        borderRadius: 'var(--mantine-radius-md)',
        backgroundColor: '#0B0E14',
        color: '#FFFFFF',
      }}
    >
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Text size="xs" c="#A8B0BF" tt="uppercase" fw={600} style={{ letterSpacing: 0.6 }}>
          How this row appears on Home
        </Text>
        {overflow > 0 && (
          <Text size="xs" c="#A8B0BF">
            Home shows the first {HOME_ROW_CEILING} items · {overflow} more not shown
          </Text>
        )}
      </Group>
      <Text size="md" fw={600} mb={8}>
        {title}
      </Text>
      <ScrollArea type="auto" scrollbars="x" offsetScrollbars>
        <Group gap={12} wrap="nowrap" align="flex-start" pb={4}>
          {shown.map((child) => (
            <Box key={child.key} style={{ width: TILE_W, flex: '0 0 auto' }}>
              <ChildThumbnail
                url={child.thumbnailUrl}
                kind={child.kind}
                title={child.title}
                width={TILE_W}
                onAir={child.isOnAir}
                dimmed={child.unavailable}
              />
              <Text size="xs" c={child.unavailable ? '#6B7280' : '#A8B0BF'} mt={6} lineClamp={2} style={{ lineHeight: 1.25 }}>
                {child.unavailable ? 'Unavailable' : child.title}
              </Text>
            </Box>
          ))}
        </Group>
      </ScrollArea>
    </Box>
  );
}

export const HomeRowPreview = memo(HomeRowPreviewInner);
